/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';

import Button from '@atlaskit/button/custom-theme-button';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

const fileHoveredColor = token('color.skeleton', colors.N20);
const dividerColor = token('color.border', colors.N40);
const colorGreen = token('color.text.accent.green', colors.G500);
const colorRed = token('color.text.accent.red', colors.R500);
const colorBlue = token('color.text.accent.blue', colors.B500);
const colorYellow = token('color.text.accent.yellow', '#7F5F01'); // Yellow800 hex
const colorDisabled = token('color.text.disabled', colors.N400A);
const backgroundColorGreen = token(
  'color.background.accent.green.subtlest',
  colors.G50
);
const backgroundColorRed = token(
  'color.background.accent.red.subtlest',
  colors.R50
);
const backgroundColorBlue = token(
  'color.background.accent.blue.subtlest',
  colors.B50
);
const backgroundColorYellow = token(
  'color.background.accent.yellow.subtlest',
  colors.Y75
);

// AK Tooltip inserts an empty div and we need to target it
// to properly add ellipsis to the file name
export const TooltipWrapper = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  > div {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const Directory = styled.li<{
  isLastDir: boolean;
}>`
  user-select: none;
  padding-bottom: ${token('space.100', '8px')};
  ${({ isLastDir }) =>
    !isLastDir &&
    `border-bottom: 1px solid ${dividerColor};
     margin-bottom: ${token('space.150', '12px')};`}
`;

/* remove this disable once https://github.com/YozhikM/stylelint-a11y/issues/38 is addressed */
/* stylelint-disable a11y/media-prefers-reduced-motion */
export const DirectoryHeader = styled(Button)`
  width: 100%;
  border: 0;
  background: none;
  display: flex;
  padding: 0;
  align-items: center;
  margin-left: 0;
  line-height: 20px;
  cursor: pointer;
  transition: all 0.2s ease-out;
  &:hover,
  &:focus {
    text-decoration: none;
    background-color: ${fileHoveredColor};
    transition: all 0.2s ease-in;
    width: 100%;
    align-items: center;
  }
  > div:last-of-type {
    display: flex;
    flex-shrink: 0;
    overflow: hidden;
  }
`;

export const DirectoryText = styled.span<{ isCollapsed: boolean }>`
  margin-left: ${token('space.025', '2px')};
  ${({ isCollapsed }) =>
    isCollapsed ? `& span { color: ${colorDisabled}; }` : ''}
`;

export const FileName = styled.span<{ isReviewed?: boolean }>`
  margin-left: ${token('space.050', '4px')};

  ${({ isReviewed }) =>
    isReviewed ? `& span { color: ${colorDisabled} }` : ''}
`;

export const DirectoryContent = styled.ul`
  margin-left: 0;
  margin-top: 6px;
  padding-left: 0;
  list-style: none;
`;

export const FileNotes = styled.div`
  margin-left: auto;
  height: 100%;
  align-items: center;
`;

export const File = styled.a<{ isActive: boolean; isHidden: boolean }>`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  padding: ${token('space.025', '2px')} ${token('space.050', '4px')}
    ${token('space.025', '2px')} ${token('space.075', '6px')};
  background-color: ${({ isActive }) =>
    isActive ? backgroundColorBlue : 'transparent'};
  border-radius: ${token('border.radius', '3px')};
  color: inherit;
  cursor: pointer;
  transition: all 0.2s ease-out;
  text-decoration: none;
  height: 20px;
  opacity: ${({ isHidden }) => (isHidden ? 0.5 : 1)};

  &:hover,
  &:focus {
    background-color: ${fileHoveredColor};
    color: inherit;
    transition: all 0.2s ease-in;
    text-decoration: none;
  }

  @media screen and (prefers-reduced-motion: reduce) {
    /* https://css-tricks.com/revisiting-prefers-reduced-motion-the-reduced-motion-media-query/ */
    transition-duration: 0.001ms;

    &:hover,
    &:focus {
      transition-duration: 0.001ms;
    }
  }

  > div:last-of-type {
    display: flex;
    flex-shrink: 0;
    overflow: hidden;
  }
`;

export const CommentsWrapper = styled.span`
  display: flex;
  align-items: center;
  margin-left: ${token('space.050', '4px')};
  height: 100%;

  /* !! Hacky overrides for AK Icons that don't support our needs !! */
  & > span[role='presentation'],
  & svg {
    vertical-align: unset;
    height: 12px;
    width: 12px;
  }
`;

export const CommentsNumber = styled.span`
  margin-top: 0;
  color: initial;
  margin-left: ${token('space.050', '4px')};
  font-weight: normal;
`;

export const LinesAdded = styled.span<{
  cutCorners: boolean;
  isColorBlindMode: boolean;
}>`
  margin-top: 0;
  ${({ cutCorners, isColorBlindMode }) => {
    let baseStyling = isColorBlindMode
      ? `background-color:  ${backgroundColorBlue};
        & span {
         color: ${colorBlue};
        }`
      : `background-color:  ${backgroundColorGreen};
        & span {
         color: ${colorGreen};
        }`;

    baseStyling = cutCorners
      ? `${baseStyling} border-radius: ${token('space.075', '6px')} 
      ${token('space.0', '0px')} ${token('space.0', '0px')} 
      ${token('space.075', '6px')};`
      : `${baseStyling} border-radius: ${token('space.075', '6px')};`;

    baseStyling = `${baseStyling} padding: 3px 4px;`;

    return baseStyling;
  }}

  /* left margin only if this is next to a comments count */
  ${CommentsWrapper as any} ~ & {
    margin-left: ${token('space.100', '8px')};
  }
`;

export const LinesRemoved = styled.span<{
  cutCorners: boolean;
  isColorBlindMode: boolean;
}>`
  margin-top: 0;
  ${({ cutCorners, isColorBlindMode }) => {
    let baseStyling = isColorBlindMode
      ? `background-color:  ${backgroundColorYellow};
        & span {
         color: ${colorYellow}
        };`
      : `background-color:  ${backgroundColorRed};
        & span {
         color: ${colorRed};
        }`;

    baseStyling = cutCorners
      ? `${baseStyling} border-radius: ${token('space.0', '0px')} 
        ${token('space.075', '6px')} ${token('space.075', '6px')} 
        ${token('space.0', '0px')};`
      : `${baseStyling} border-radius: ${token('space.075', '6px')};`;

    baseStyling = `${baseStyling} padding: 3px 4px;`;

    return baseStyling;
  }}
`;

export const NonStyledList = styled.ul`
  list-style: none;
  padding-left: 0;
`;
